import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  AttachFile,
  CheckCircleOutline,
  HelpOutline,
  Search,
  ThumbDownOffAlt,
  ThumbUpOffAlt,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { helpAPI } from '../../services/api';

const pageSx = {
  minHeight: '100%',
  bgcolor: '#f3f6fa',
  mx: { xs: -2, md: -3 },
  px: { xs: 2, md: 3 },
  py: { xs: 2, md: 3 },
};

const surfaceSx = {
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'rgba(15, 23, 42, 0.08)',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  bgcolor: '#fff',
};

function renderInline(text) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <Box component="strong" key={i} sx={{ fontWeight: 700, color: 'text.primary' }}>
          {part.slice(2, -2)}
        </Box>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function isListLine(line) {
  return /^[-*]\s+/.test(line) || /^\d+\.\s+/.test(line);
}

function ListBlock({ lines, keyPrefix }) {
  const ordered = /^\d+\.\s+/.test(lines[0] || '');
  return (
    <Box
      component={ordered ? 'ol' : 'ul'}
      sx={{
        m: 0,
        pl: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      {lines.map((l, i) => (
        <Box
          key={`${keyPrefix}-${i}`}
          component="li"
          sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}
        >
          <Box
            sx={{
              mt: '3px',
              minWidth: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: 'rgba(25, 118, 210, 0.1)',
              color: 'primary.main',
              fontSize: 12,
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {ordered ? i + 1 : '•'}
          </Box>
          <Typography variant="body1" sx={{ lineHeight: 1.65, color: 'text.primary', pt: 0.15 }}>
            {renderInline(l.replace(/^([-*]|\d+\.)\s+/, ''))}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function renderBlock(block, idx) {
  const normalized = String(block || '').replace(/\r\n/g, '\n').trim();
  if (!normalized) return null;

  // Heading may share a block with following content if blank lines were lost in storage
  if (normalized.startsWith('## ')) {
    const nl = normalized.indexOf('\n');
    const headingLine = (nl >= 0 ? normalized.slice(0, nl) : normalized).replace(/^##\s+/, '').trim();
    const rest = nl >= 0 ? normalized.slice(nl + 1).trim() : '';
    return (
      <Box key={idx}>
        <Typography
          variant="subtitle1"
          sx={{
            mb: rest ? 1.5 : 0,
            fontWeight: 800,
            color: 'text.primary',
            letterSpacing: '-0.01em',
          }}
        >
          {headingLine}
        </Typography>
        {rest ? renderBlock(rest, `${idx}-rest`) : null}
      </Box>
    );
  }

  if (normalized.startsWith('|') && normalized.includes('\n|')) {
    const lines = normalized.split('\n').filter((l) => l.trim().startsWith('|'));
    const rows = lines
      .filter((l) => !/^\|\s*-+/.test(l))
      .map((l) =>
        l
          .split('|')
          .slice(1, -1)
          .map((c) => c.trim())
      );
    if (!rows.length) return null;
    return (
      <Box
        key={idx}
        sx={{
          overflowX: 'auto',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            {rows.map((cells, rIdx) => (
              <Box component="tr" key={rIdx}>
                {cells.map((cell, cIdx) => (
                  <Box
                    component={rIdx === 0 ? 'th' : 'td'}
                    key={cIdx}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      px: 1.5,
                      py: 1.25,
                      textAlign: 'left',
                      fontSize: '0.9rem',
                      fontWeight: rIdx === 0 ? 700 : 400,
                      bgcolor: rIdx === 0 ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                      color: 'text.primary',
                    }}
                  >
                    {renderInline(cell)}
                  </Box>
                ))}
              </Box>
            ))}
          </tbody>
        </Box>
      </Box>
    );
  }

  const lines = normalized.split('\n').map((l) => l.trimEnd());
  if (lines.length && lines.every((l) => !l.trim() || isListLine(l.trim()))) {
    return (
      <ListBlock
        key={idx}
        lines={lines.map((l) => l.trim()).filter(Boolean)}
        keyPrefix={String(idx)}
      />
    );
  }

  // Also split " - item - item" collapsed onto one line (legacy bad storage)
  if (!normalized.includes('\n') && /\s+-\s+/.test(normalized) && normalized.trim().startsWith('- ')) {
    const parts = normalized
      .split(/\s+-\s+/)
      .map((p, i) => (i === 0 ? p.replace(/^-\s+/, '').trim() : p.trim()))
      .filter(Boolean)
      .map((p) => `- ${p}`);
    if (parts.length > 1) {
      return <ListBlock key={idx} lines={parts} keyPrefix={String(idx)} />;
    }
  }

  return (
    <Typography key={idx} variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
      {renderInline(normalized)}
    </Typography>
  );
}

function SimpleMarkdown({ text }) {
  const blocks = String(text || '')
    .replace(/\r\n/g, '\n')
    .split(/\n\n+/)
    .map((b) => b.trim())
    .filter(Boolean);

  return <Stack spacing={2}>{blocks.map((block, idx) => renderBlock(block, idx))}</Stack>;
}

function ArticleDetail({ articleId, onBack }) {
  const queryClient = useQueryClient();
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [notHelpfulComment, setNotHelpfulComment] = useState('');
  const [showNoForm, setShowNoForm] = useState(false);

  const articleQuery = useQuery(['help-article', articleId], async () => {
    const res = await helpAPI.getArticle(articleId);
    return res.data.data;
  });

  const feedbackMutation = useMutation(
    (payload) => helpAPI.sendFeedback(articleId, payload),
    {
      onSuccess: () => {
        setFeedbackSent(true);
        toast.success('Thanks for the feedback');
        queryClient.invalidateQueries(['help-article', articleId]);
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Failed to send feedback'),
    }
  );

  if (articleQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (articleQuery.error || !articleQuery.data) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        Article not found or not available for your role.
      </Alert>
    );
  }

  const a = articleQuery.data;

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => onBack()}
        sx={{
          mb: 2,
          textTransform: 'none',
          fontWeight: 700,
          color: 'text.secondary',
          '&:hover': { bgcolor: 'rgba(15,23,42,0.04)' },
        }}
      >
        Back to Help
      </Button>

      <Paper sx={{ ...surfaceSx, p: { xs: 2.5, md: 4 }, mb: 2.5 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {(a.topicLabels || []).map((t) => (
            <Chip
              key={t}
              size="small"
              label={t}
              sx={{
                bgcolor: 'rgba(25, 118, 210, 0.08)',
                color: 'primary.dark',
                fontWeight: 700,
                borderRadius: 2,
              }}
            />
          ))}
          {a.lastVerified ? (
            <Chip
              size="small"
              icon={<CheckCircleOutline sx={{ fontSize: '16px !important' }} />}
              label={`Verified ${String(a.lastVerified).slice(0, 10)}`}
              variant="outlined"
              sx={{ borderRadius: 2, fontWeight: 600 }}
            />
          ) : null}
        </Stack>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.25,
            mb: 2.5,
            fontSize: { xs: '1.6rem', md: '2rem' },
          }}
        >
          {a.title}
        </Typography>

        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: 2.5,
            bgcolor: 'rgba(25, 118, 210, 0.06)',
            border: '1px solid',
            borderColor: 'rgba(25, 118, 210, 0.16)',
            mb: a.bodyMarkdown ? 3 : 0,
          }}
        >
          <Typography
            variant="overline"
            sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: 1 }}
          >
            Short answer
          </Typography>
          <Box sx={{ mt: 0.75 }}>
            <SimpleMarkdown text={a.shortAnswer} />
          </Box>
        </Box>

        {a.bodyMarkdown ? <SimpleMarkdown text={a.bodyMarkdown} /> : null}
      </Paper>

      {(a.attachments || []).length > 0 ? (
        <Paper sx={{ ...surfaceSx, p: 2.5, mb: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
            Attachments
          </Typography>
          <Stack spacing={1}>
            {a.attachments.map((att) => (
              <Button
                key={att.id}
                variant="outlined"
                startIcon={<AttachFile />}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                }}
                onClick={async () => {
                  try {
                    const res = await helpAPI.downloadAttachment(att.id);
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = att.original_name || 'attachment';
                    link.click();
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    toast.error('Download failed');
                  }
                }}
              >
                {att.original_name}
              </Button>
            ))}
          </Stack>
        </Paper>
      ) : null}

      {(a.related || []).length > 0 ? (
        <Paper sx={{ ...surfaceSx, p: 2.5, mb: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
            Related articles
          </Typography>
          <Stack spacing={1}>
            {a.related.map((r) => (
              <Box
                key={r.id}
                onClick={() => onBack(r.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1.75,
                  py: 1.25,
                  borderRadius: 2,
                  cursor: 'pointer',
                  bgcolor: 'rgba(15,23,42,0.02)',
                  border: '1px solid',
                  borderColor: 'transparent',
                  '&:hover': {
                    borderColor: 'rgba(25,118,210,0.25)',
                    bgcolor: 'rgba(25,118,210,0.04)',
                  },
                }}
              >
                <Box>
                  <Typography fontWeight={700}>{r.title}</Typography>
                  {r.shortAnswer ? (
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 520 }}>
                      {r.shortAnswer}
                    </Typography>
                  ) : null}
                </Box>
                <ArrowForward fontSize="small" color="action" />
              </Box>
            ))}
          </Stack>
        </Paper>
      ) : null}

      <Paper sx={{ ...surfaceSx, p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={800} gutterBottom>
          Was this helpful?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your feedback helps us improve answers for the whole team.
        </Typography>
        {feedbackSent ? (
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            Thanks — feedback recorded.
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1.25}>
              <Button
                variant="contained"
                color="success"
                startIcon={<ThumbUpOffAlt />}
                disabled={feedbackMutation.isLoading}
                onClick={() => feedbackMutation.mutate({ helpful: true })}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 2.5 }}
              >
                Yes, helpful
              </Button>
              <Button
                variant={showNoForm ? 'contained' : 'outlined'}
                color="inherit"
                startIcon={<ThumbDownOffAlt />}
                disabled={feedbackMutation.isLoading}
                onClick={() => setShowNoForm(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 2.5,
                  borderColor: 'divider',
                }}
              >
                Not really
              </Button>
            </Stack>
            {showNoForm ? (
              <Stack spacing={1.25}>
                <TextField
                  size="small"
                  label="What were you trying to do?"
                  placeholder="One short sentence helps us add the right article"
                  value={notHelpfulComment}
                  onChange={(e) => setNotHelpfulComment(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <Button
                  variant="contained"
                  color="primary"
                  disabled={feedbackMutation.isLoading}
                  onClick={() => {
                    if (!notHelpfulComment.trim()) {
                      toast.error('Please tell us what you were trying to do');
                      return;
                    }
                    feedbackMutation.mutate({
                      helpful: false,
                      comment: notHelpfulComment.trim(),
                    });
                  }}
                  sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                >
                  Submit feedback
                </Button>
              </Stack>
            ) : null}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}

export default function HelpCenterPage() {
  const navigate = useNavigate();
  const { articleId: routeArticleId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [topic, setTopic] = useState(searchParams.get('topic') || '');
  const [suggest, setSuggest] = useState('');
  const selectedId = routeArticleId || searchParams.get('id') || '';

  const topicsQuery = useQuery(['help-topics'], async () => {
    const res = await helpAPI.getTopics();
    return res.data.data || [];
  });

  const listQuery = useQuery(['help-articles', q, topic], async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (topic) params.topic = topic;
    const res = await helpAPI.listArticles(params);
    return res.data.data || [];
  });

  const suggestMutation = useMutation((payload) => helpAPI.suggestQuestion(payload), {
    onSuccess: () => {
      toast.success('Saved — Admin will review and add documentation');
      setSuggest('');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Could not save question'),
  });

  const articles = listQuery.data || [];

  const openArticle = (id) => {
    navigate(`/help/article/${encodeURIComponent(id)}`);
  };

  const runSearch = () => {
    setSearchParams((prev) => {
      const n = new URLSearchParams(prev);
      if (q.trim()) n.set('q', q.trim());
      else n.delete('q');
      if (topic) n.set('topic', topic);
      else n.delete('topic');
      return n;
    });
    listQuery.refetch();
  };

  return (
    <Box sx={pageSx}>
      {selectedId ? (
        <ArticleDetail
          articleId={selectedId}
          onBack={(nextId) => {
            if (nextId) openArticle(nextId);
            else navigate('/help');
          }}
        />
      ) : (
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Paper
            sx={{
              ...surfaceSx,
              p: { xs: 2.5, md: 3.5 },
              mb: 3,
              background:
                'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(255,255,255,1) 55%)',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <HelpOutline />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
                  Help Center
                </Typography>
                <Typography color="text.secondary">
                  Plain-language answers for teachers, sales, finance, and admins.
                </Typography>
              </Box>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ mt: 2.5 }}>
              <TextField
                fullWidth
                placeholder="Search: zoom substitute, merge, leave, invoice…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runSearch();
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2.5, bgcolor: '#fff' },
                }}
              />
              <Button
                variant="contained"
                onClick={runSearch}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: 2.5,
                  px: 3,
                  minWidth: { md: 120 },
                }}
              >
                Search
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
              <Chip
                label="All topics"
                clickable
                onClick={() => setTopic('')}
                color={!topic ? 'primary' : 'default'}
                variant={!topic ? 'filled' : 'outlined'}
                sx={{ borderRadius: 2, fontWeight: 700 }}
              />
              {(topicsQuery.data || []).map((t) => (
                <Chip
                  key={t.id}
                  label={t.label}
                  clickable
                  onClick={() => setTopic(t.id)}
                  color={topic === t.id ? 'primary' : 'default'}
                  variant={topic === t.id ? 'filled' : 'outlined'}
                  sx={{ borderRadius: 2, fontWeight: 600 }}
                />
              ))}
            </Stack>
          </Paper>

          {listQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : null}

          {!listQuery.isLoading && articles.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              No matching answers yet. Your search was saved for Admin to document. You can also
              suggest a question below.
            </Alert>
          ) : null}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {articles.map((a) => (
              <Grid item xs={12} md={6} key={a.id}>
                <Paper
                  onClick={() => openArticle(a.id)}
                  sx={{
                    ...surfaceSx,
                    p: 2.5,
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
                    {(a.topicLabels || []).slice(0, 2).map((t) => (
                      <Chip
                        key={t}
                        size="small"
                        label={t}
                        sx={{
                          bgcolor: 'rgba(25,118,210,0.08)',
                          color: 'primary.dark',
                          fontWeight: 700,
                          borderRadius: 1.5,
                        }}
                      />
                    ))}
                  </Stack>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 1, lineHeight: 1.3 }}>
                    {a.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, lineHeight: 1.55, minHeight: 44 }}
                  >
                    {String(a.shortAnswer || '').slice(0, 140)}
                    {String(a.shortAnswer || '').length > 140 ? '…' : ''}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5} color="primary.main">
                    <Typography variant="body2" fontWeight={800}>
                      Read answer
                    </Typography>
                    <ArrowForward sx={{ fontSize: 16 }} />
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Paper sx={{ ...surfaceSx, p: { xs: 2.5, md: 3 } }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Couldn&apos;t find what you need?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Suggest a question. Admin reviews unanswered items and adds documentation for everyone.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <TextField
                fullWidth
                placeholder="What were you trying to do?"
                value={suggest}
                onChange={(e) => setSuggest(e.target.value)}
                InputProps={{ sx: { borderRadius: 2.5 } }}
              />
              <Button
                variant="contained"
                disabled={suggestMutation.isLoading || suggest.trim().length < 3}
                onClick={() =>
                  suggestMutation.mutate({ question: suggest.trim(), source: 'suggest_question' })
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: 2.5,
                  px: 3,
                  whiteSpace: 'nowrap',
                }}
              >
                Suggest question
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
